import { GridFilterModel } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { StatCard } from '../../../../shared/stat-card';
import {
  getGroupHealthSettings,
  getGroupHealthSummary,
  UserKeys,
} from '../../../../api/users-api';
import { GroupHealth } from '../../../../shared/health-chip';

import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import PersonOffOutlined from '@mui/icons-material/PersonOffOutlined';
import GroupRemoveOutlined from '@mui/icons-material/GroupRemoveOutlined';
import NightlightRound from '@mui/icons-material/NightlightRound';
import CheckOutlined from '@mui/icons-material/CheckOutlined';

const TILE_PLACEHOLDER_COUNT = 5;

const tileRowSx = {
  mb: 2,
  width: '100%',
  flexWrap: 'nowrap',
  overflowX: 'auto',
} as const;

const tileSx = { flex: '1 1 0', minWidth: 200 };

export interface GroupHealthTilesProps {
  enabled: boolean;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
}

export default function GroupHealthTiles({
  enabled,
  filterModel,
  onFilterModelChange,
}: GroupHealthTilesProps) {
  const { data: summary, isSuccess: summaryLoaded } = useQuery({
    queryKey: [UserKeys.GroupHealthSummary],
    queryFn: getGroupHealthSummary,
    enabled,
  });

  const { data: settings, isSuccess: settingsLoaded } = useQuery({
    queryKey: [UserKeys.GroupHealthSettings],
    queryFn: getGroupHealthSettings,
    enabled,
  });

  if (!enabled) {
    return null;
  }

  if (!summaryLoaded || !settingsLoaded || !summary || !settings) {
    return (
      <Stack
        direction="row"
        spacing={2}
        aria-busy="true"
        aria-label="Loading group health"
        sx={tileRowSx}
      >
        {Array.from({ length: TILE_PLACEHOLDER_COUNT }, (_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            sx={{ ...tileSx, height: 72, borderRadius: 2 }}
          />
        ))}
      </Stack>
    );
  }

  const showOwnerless = settings.flagOwnerless;
  const showManagerless = settings.flagManagerless;
  const showInactive = settings.flagInactive;

  const healthFilterValue = filterModel.items.find(
    (item) => item.field === 'health'
  )?.value as GroupHealth | undefined;

  const totalActive = !healthFilterValue;

  const setHealthFilter = (value?: GroupHealth) => {
    const items = filterModel.items.filter((item) => item.field !== 'health');
    if (value) {
      items.push({ field: 'health', operator: 'equals', value });
    }
    onFilterModelChange({ ...filterModel, items });
  };

  return (
    <Stack direction="row" spacing={2} sx={tileRowSx}>
      <StatCard
        color="teal"
        label="Groups"
        value={summary.total}
        icon={<GroupOutlined />}
        onClick={totalActive ? undefined : () => setHealthFilter(undefined)}
        active={totalActive}
        disabled={totalActive}
        sx={tileSx}
      />
      {showOwnerless && (
        <StatCard
          color="rose"
          label="Ownerless"
          value={summary.ownerless}
          icon={<PersonOffOutlined />}
          onClick={
            healthFilterValue === 'ownerless'
              ? undefined
              : () => setHealthFilter('ownerless')
          }
          active={healthFilterValue === 'ownerless'}
          disabled={healthFilterValue === 'ownerless'}
          sx={tileSx}
        />
      )}
      {showManagerless && (
        <StatCard
          color="orange"
          label="Managerless"
          value={summary.managerless}
          icon={<GroupRemoveOutlined />}
          onClick={
            healthFilterValue === 'managerless'
              ? undefined
              : () => setHealthFilter('managerless')
          }
          active={healthFilterValue === 'managerless'}
          disabled={healthFilterValue === 'managerless'}
          sx={tileSx}
        />
      )}
      {showInactive && (
        <StatCard
          color="gray"
          label="Inactive"
          value={summary.inactive}
          icon={<NightlightRound />}
          onClick={
            healthFilterValue === 'inactive'
              ? undefined
              : () => setHealthFilter('inactive')
          }
          active={healthFilterValue === 'inactive'}
          disabled={healthFilterValue === 'inactive'}
          sx={tileSx}
        />
      )}
      <StatCard
        color="green"
        label="Healthy"
        value={summary.healthy}
        icon={<CheckOutlined />}
        onClick={
          healthFilterValue === 'healthy'
            ? undefined
            : () => setHealthFilter('healthy')
        }
        active={healthFilterValue === 'healthy'}
        disabled={healthFilterValue === 'healthy'}
        sx={tileSx}
      />
    </Stack>
  );
}
